import React, { useState, useEffect } from "react";
import { X } from "react-bootstrap-icons";
import { Modal as BootstrapModal } from "react-bootstrap";
import Switch from "@/components/common/switch/switch";
import { syncUser } from "@/services/UserService";
import { useUser } from "@/context/UserContext";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { isMobile } from "react-device-detect";

const NICKNAME_MIN = 2;
const NICKNAME_MAX = 30;

export interface ChooseNicknameModalProps {
    show: boolean;
    onClose: () => void;
    nickname?: string | null;
    showScoreLeaderboard?: boolean;
}

export default function ChooseNicknameModal({
    show,
    onClose,
    nickname = "",
    showScoreLeaderboard = true,
}: ChooseNicknameModalProps) {
    const [inputValue, setInputValue] = useState(nickname?.trim() ?? "");
    const [showLeaderboard, setShowLeaderboard] = useState(showScoreLeaderboard ?? true);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { userState, setUserState, fetchUserData } = useUser();
    const queryClient = useQueryClient();

    // Sync local state from props whenever modal is shown or props change so we always display saved values
    useEffect(() => {
        if (show) {
            setInputValue(nickname?.trim() ?? "");
            setShowLeaderboard(showScoreLeaderboard ?? true);
            setError("");
        }
    }, [show, nickname, showScoreLeaderboard]);

    const validate = (): boolean => {
        const v = inputValue.trim();
        if (!v) {
            setError("Nickname is required");
            return false;
        }
        if (v.length < NICKNAME_MIN) {
            setError(`Nickname must be at least ${NICKNAME_MIN} characters`);
            return false;
        }
        if (v.length > NICKNAME_MAX) {
            setError(`Nickname must be no more than ${NICKNAME_MAX} characters`);
            return false;
        }
        setError("");
        return true;
    };

    const handleConfirm = async () => {
        if (!validate() || !userState?.id || isSubmitting) return;
        setIsSubmitting(true);
        const newNickname = inputValue.trim();
        try {
            await syncUser({
                id: userState.id,
                nickname: newNickname,
                showScoreLeaderboard: showLeaderboard,
            });
            // Update context so header, profile, etc. show the new values immediately (fetchUserData only runs once)
            setUserState(userState, false, {
                nickname: newNickname,
                showScoreLeaderboard: showLeaderboard,
            });
            await fetchUserData(userState.id);
            queryClient.invalidateQueries({ queryKey: ["user-notif-settings", userState.id] });
            toast.success("Profile updated");
            onClose();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    const content = (
        <form
            className="tw-bg-[#2E2E2E] tw-rounded-2xl tw-p-6 tw-pt-10 tw-text-white tw-w-full tw-max-w-md tw-mx-auto tw-relative"
            onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
            }}
        >
            <button
                type="button"
                onClick={onClose}
                className="tw-absolute tw-top-3 tw-right-3 tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-full tw-text-gray-300 hover:tw-text-white tw-transition-colors"
                aria-label="Close"
            >
                <X className="tw-w-5 tw-h-5" />
            </button>
            <h2 className="tw-text-center tw-text-[#D9D9D9] tw-text-[15px] tw-font-bold tw-mb-2">
                Choose your nickname
            </h2>
            <p className="tw-text-center tw-text-[#99A1AF] !tw-text-xs tw-mb-4">
                This is the public name other users see next to your comment:
            </p>
            <div className="tw-relative tw-mb-4">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="tw-w-full tw-border tw-border-[#4a4a4a] tw-rounded-lg tw-py-3 tw-pl-4 tw-pr-10 tw-text-[#555E6780] tw-font-semibold tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#F69929]"
                    maxLength={NICKNAME_MAX}
                    aria-invalid={!!error}
                    aria-describedby={error ? "nickname-error" : undefined}
                />
                {inputValue.length > 0 && (
                    <button
                        type="button"
                        className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-rounded-full tw-p-1 tw-text-white"
                        onClick={() => setInputValue("")}
                        aria-label="Clear nickname"
                    >
                        <X className="tw-w-5 tw-h-5" />
                    </button>
                )}
            </div>
            {error && (
                <p id="nickname-error" className="tw-text-red-400 tw-text-sm tw-mb-4" role="alert">
                    {error}
                </p>
            )}
            <div
                className="tw-flex tw-items-center tw-gap-4 tw-mb-6"
                style={{ ["--switch-active-color" as string]: "#F69929" }}
            >
                <div className="tw-shrink-0 [&_.toggle-switch]:tw-w-[54px] [&_.toggle-switch]:tw-min-w-[54px] [&_.toggle-switch]:tw-h-[28.5px] [&_.toggle-switch]:tw-min-h-[28.5px] [&_.toggle-switch]:tw-shrink-0">
                    <Switch
                        checked={showLeaderboard}
                        onChange={(e) => setShowLeaderboard(e.target.checked)}
                    />
                </div>
                <span className="tw-text-gray-300 tw-text-sm">Show my score on the leaderboard</span>
            </div>
            <button
                type="submit"
                disabled={isSubmitting}
                className="tw-w-full tw-bg-[#F69929] hover:tw-bg-[#e88a00] tw-text-white tw-font-semibold tw-py-3 tw-rounded-lg tw-transition disabled:tw-opacity-60"
            >
                {isSubmitting ? "Saving…" : "Confirm"}
            </button>
        </form>
    );

    if (!show) return null;

    if (isMobile) {
        return (
            <div className="tw-fixed tw-inset-0 tw-z-[1050] tw-flex tw-items-end tw-justify-center tw-bg-black/60">
                <div className="tw-w-full tw-max-w-md tw-mx-auto">
                    <div className="tw-shadow-2xl tw-max-h-[80vh] tw-overflow-y-auto">
                        {content}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <BootstrapModal
            show={show}
            onHide={onClose}
            centered
            contentClassName="!tw-bg-transparent !tw-border-0 tw-shadow-none tw-outline-none"
            dialogClassName="tw-max-w-md tw-border-0 tw-outline-none"
        >
            <BootstrapModal.Body className="tw-p-0 tw-bg-transparent tw-border-0">{content}</BootstrapModal.Body>
        </BootstrapModal>
    );
}
